import { businessIdsHtml, escapeHtml as esc, printHtml, type DocumentSalon } from '../../utils/invoice'
import { brandColors } from '../../data/brandColors'
import { saleMethods, vatOfLines } from '../../core/rules/kassa'
import { money } from '../../core/format/currency'
import { clock, methodLabel } from './kassaLabels'
import type { Sale } from '../../core/types/kassa'

type Salon = DocumentSalon

/**
 * De kassabon als document (printen of als bestand bewaren/mailen), met wat een bon wettelijk
 * nodig heeft: salongegevens, KvK, btw-id, bonnummer, datum en btw per tarief. Een gecorrigeerde
 * bon blijft bestaan en zegt dat er groot boven. Met de echte server wordt dit een PDF (§9.6).
 */
export function saleReceiptHtml(s: Sale, salon: Salon, dateLabel: string, performer = ''): string {
  const voided = s.status === 'gecorrigeerd'
  const vat = vatOfLines(s.lines)
  // Verkochte cadeaubonnen staan in de volgorde van de regels: zo krijgt elke regel zijn eigen code.
  const issued = [...(s.issuedVouchers ?? [])]
  const rows = s.lines
    .map((l) => {
      const qty = l.kind === 'product' ? l.qty : 1
      const card = l.kind === 'giftcard' ? issued.shift() : undefined
      const note = [
        l.kind === 'treatment' && l.fromKuur ? 'uit kuur' : '',
        l.kind === 'giftcard' ? `code eindigt op ${card?.last4 ?? '…'} · btw bij inwisselen` : '',
        l.discountReason ? `korting ${money((l.listPriceCents - l.priceCents) * qty)}: ${l.discountReason}` : '',
      ]
        .filter(Boolean)
        .join(' · ')
      return `<tr><td>${qty > 1 ? `${qty} × ` : ''}${esc(l.name)}${note ? `<div class="muted small">${esc(note)}</div>` : ''}</td><td class="r">${l.vatRate}%</td><td class="r">${esc(money(l.totalCents))}</td></tr>`
    })
    .join('')
  const paid = [
    s.voucherCents > 0 ? `<tr><td class="muted">Cadeaubon …${esc(s.voucherLast4 ?? '')}</td><td class="r">${esc(money(s.voucherCents))}</td></tr>` : '',
    s.creditCents ? `<tr><td class="muted">Tegoed</td><td class="r">${esc(money(s.creditCents))}</td></tr>` : '',
    ...(['pin', 'contant'] as const).map((m) => {
      const cents = saleMethods(s)[m]
      return cents > 0 ? `<tr><td class="muted">${esc(methodLabel[m])}</td><td class="r">${esc(money(cents))}</td></tr>` : ''
    }),
    s.cashReceivedCents !== undefined
      ? `<tr><td class="muted">Ontvangen · wisselgeld</td><td class="r">${esc(money(s.cashReceivedCents))} · ${esc(money(s.changeCents ?? 0))}</td></tr>`
      : '',
  ].join('')

  return `<!doctype html><html lang="nl"><head><meta charset="utf-8"><title>Bon ${esc(s.number)}</title>
<style>
@page{margin:12mm}
body{font-family:Inter,system-ui,sans-serif;color:${brandColors.ink};margin:40px auto;max-width:420px;font-size:14px;line-height:1.5}
@media print{body{margin:0 auto}}
h1{font-family:Fraunces,Georgia,serif;font-weight:300;font-size:26px;margin:0 0 4px}
.muted{color:${brandColors.muted}}.small{font-size:12px}
table{width:100%;border-collapse:collapse;margin-top:20px}td{text-align:left;padding:7px 0;border-bottom:1px solid ${brandColors.line};vertical-align:top}
td.r{text-align:right;white-space:nowrap;padding-left:12px}.tot td{border:0;padding:3px 0}.big td{font-size:16px;padding-top:10px}
.void{margin-top:16px;padding:8px 12px;border:1px solid ${brandColors.ink};text-align:center;letter-spacing:.04em}
</style></head><body>
<strong>${esc(salon.name)}</strong><div class="muted small">${esc(salon.address)} · ${esc(salon.email)}<br>${businessIdsHtml(salon, ' · ')}</div>
<h1 style="margin-top:24px">Kassabon</h1>
<div class="muted">${esc(s.number)} · ${esc(dateLabel)} ${esc(clock(s.at))}${s.customerName ? ` · ${esc(s.customerName)}` : ''}</div>
${performer ? `<div class="muted small">Behandeld door ${esc(performer)}</div>` : ''}
${voided ? `<div class="void">GECORRIGEERD${s.correction ? ` · ${esc(s.correction.reason)}` : ''}</div>` : ''}
<table><tbody>${rows}</tbody></table>
<table><tbody class="tot">
<tr class="big"><td>Totaal</td><td class="r">${esc(money(s.totalCents))}</td></tr>
${vat.map((v) => `<tr><td class="muted">Waarvan btw ${v.vatRate}%</td><td class="r">${esc(money(v.vatCents))}</td></tr>`).join('')}
${paid}
</tbody></table>
<p class="muted small" style="margin-top:32px">Dank je wel voor je bezoek.</p>
</body></html>`
}

/** `performer`: "Behandeld door …" (gedeelde salon). Leeg = niet op de bon. */
export const printSaleReceipt = (s: Sale, salon: Salon, dateLabel: string, performer = '') =>
  printHtml(saleReceiptHtml(s, salon, dateLabel, performer), `bon-${s.number}.html`, 'Kassabon')
